import React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Checkbox,
    Chip,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PostAddIcon from '@mui/icons-material/PostAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {useTranslation} from 'react-i18next';
import type {TableComponentProps, EmployeeColumnKey, SortableKey} from '../../hooks/useEmployeeHandlers';
import {EMPLOYEE_COLUMN_DEFS, useTableSort} from '../../hooks/useEmployeeHandlers';
import TableComponentMobile from './mobile/TableComponentMobile';
import {getAvatarColors, getInitials} from '../../utils/avatarUtils';

const TableComponent: React.FC<TableComponentProps> = ({
                                                           sx,
                                                           ariaLabel,
                                                           employees,
                                                           loading,
                                                           error,
                                                           selected,
                                                           page = 1,
                                                           rowsPerPage = 20,
                                                           searchQuery = '',
                                                           visibleColumns,
                                                           handleSelectAllClick,
                                                           handleClick,
                                                           isSelected,
                                                           handleEdit,
                                                           handleDelete,
                                                           handleView,
                                                           handleAddContract,
                                                       }) => {
    const {t} = useTranslation();
    const {
        anchorEl,
        selectedRowId,
        open,
        order,
        orderBy,
        handleRequestSort,
        filteredEmployees,
        paginatedEmployees,
        handleMenuClick,
        handleMenuClose,
        handleMenuAction,
    } = useTableSort({employees, searchQuery, page, rowsPerPage});

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const activeColumns = EMPLOYEE_COLUMN_DEFS.filter((c) => visibleColumns[c.key]);
    const totalColumnCount = 2 /* checkbox + actions */ + 1 /* employee (avatar+name) */ + activeColumns.length;

    const renderCellValue = (row: TableComponentProps['employees'][number], key: EmployeeColumnKey) => {
        switch (key) {
            case 'email':
                return row.email;
            case 'phone':
                return row.phone ? `(+383)${row.phone}` : '-';
            case 'employmentType':
                return row.employmentType;
            case 'status':
                return (
                    <Chip
                        label={row.status}
                        size="small"
                        color={row.status === 'Active' || row.status === 'ACTIVE' || row.status === 'active' ? 'success' : 'error'}
                        variant="outlined"
                    />
                );
            case 'branch':
                return row.branch;
            default:
                return null;
        }
    };

    const SortCell = ({label, field}: { label: string; field: SortableKey }) => (
        <TableCell sortDirection={orderBy === field ? order : false}>
            <TableSortLabel
                active={orderBy === field}
                direction={orderBy === field ? order : 'asc'}
                onClick={() => handleRequestSort(field)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    if (isMobile) {
        return (
            <>
                <TableComponentMobile
                    employees={filteredEmployees}
                    loading={loading}
                    error={error}
                    selected={selected}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    searchQuery={searchQuery}
                    handleClick={handleClick}
                    isSelected={isSelected}
                    handleEdit={handleEdit}
                    handleView={handleView}
                    handleAddContract={handleAddContract}
                />
            </>
        );
    }

    return (
        <>
            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
            {(
                <Box sx={{overflowX: 'auto'}}>
                    <Table sx={{...sx, '& .MuiTableCell-root': {whiteSpace: 'normal', wordBreak: 'break-word'}}}
                           aria-label={ariaLabel}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        indeterminate={selected.length > 0 && selected.length < filteredEmployees.length}
                                        checked={filteredEmployees.length > 0 && selected.length === filteredEmployees.length}
                                        onChange={handleSelectAllClick}
                                        inputProps={{'aria-label': t('employee.table.ariaSelectAll')}}
                                    />
                                </TableCell>
                                <SortCell label={t('employee.table.columnEmployee')} field="firstName"/>
                                {activeColumns.map((c) => (
                                    <SortCell key={c.key} label={t(c.labelKey)} field={c.field}/>
                                ))}
                                <TableCell align="center">{t('workEvidence.approval.table.header.action')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <>
                                    {Array.from({length: 5}).map((_, i) => (
                                        <TableRow key={`skeleton-${i}`}>
                                            <TableCell padding="checkbox"><Skeleton variant="rectangular" width={20}
                                                                                    height={20}/></TableCell>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                                    <Skeleton variant="circular" width={36} height={36}/>
                                                    <Skeleton variant="text" width={120}/>
                                                </Box>
                                            </TableCell>
                                            {activeColumns.map((c) => (
                                                <TableCell key={c.key}><Skeleton variant="text"/></TableCell>
                                            ))}
                                            <TableCell align="center"><Skeleton variant="circular" width={24}
                                                                                height={24}/></TableCell>
                                        </TableRow>
                                    ))}
                                </>
                            ) : filteredEmployees.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={totalColumnCount} align="center" sx={{py: 6}}>
                                            <PersonOffIcon sx={{fontSize: 48, color: 'text.disabled', mb: 1}}/>
                                            {searchQuery.trim() ? (
                                                <>
                                                    <Typography variant="body1" color="text.secondary">
                                                        {t('employee.table.noResultsFor', {query: searchQuery})}</Typography>
                                                    <Typography variant="body2" color="text.disabled">
                                                        {t('employee.table.tryDifferentSearch')}</Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="body1" color="text.secondary">
                                                        {t('employee.table.noEmployeesFound')}</Typography>
                                                    <Typography variant="body2" color="text.disabled">
                                                        {t('employee.table.tryAdjustingOrAdd')}</Typography>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedEmployees.map((row) => {
                                        const isItemSelected = isSelected(row.id);
                                        const labelId = `employee-checkbox-${row.id}`;
                                        const avatarColors = getAvatarColors(`${row.firstName}${row.lastName}${row.id}`);
                                        return (
                                            <TableRow
                                                hover
                                                onClick={(event) => handleClick(event, row.id)}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row.id}
                                                selected={isItemSelected}
                                                sx={{cursor: 'pointer'}}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        color="primary"
                                                        checked={isItemSelected}
                                                        inputProps={{'aria-labelledby': labelId}}
                                                    />
                                                </TableCell>
                                                <TableCell component="th" id={labelId} scope="row">
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                                        <Avatar sx={{
                                                            width: 36,
                                                            height: 36,
                                                            bgcolor: avatarColors.bg,
                                                            color: avatarColors.color,
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600,
                                                        }}>
                                                            {getInitials(row.firstName, row.lastName)}
                                                        </Avatar>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {row.firstName} {row.lastName}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                {activeColumns.map((c) => (
                                                    <TableCell key={c.key}>{renderCellValue(row, c.key)}</TableCell>
                                                ))}
                                                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                    <IconButton
                                                        aria-label="more"
                                                        id={`action-button-${row.id}`}
                                                        aria-controls={open && selectedRowId === row.id ? `action-menu-${row.id}` : undefined}
                                                        aria-expanded={open && selectedRowId === row.id ? 'true' : undefined}
                                                        aria-haspopup="true"
                                                        onClick={(e) => handleMenuClick(e, row.id)}
                                                    >
                                                        <MoreVertIcon/>
                                                    </IconButton>
                                                    <Menu
                                                        id={`action-menu-${row.id}`}
                                                        anchorEl={anchorEl}
                                                        open={open && selectedRowId === row.id}
                                                        onClose={handleMenuClose}
                                                        slotProps={{
                                                            paper: {
                                                                style: {
                                                                    maxHeight: 48 * 4.5,
                                                                    width: '20ch'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem
                                                            onClick={() => handleMenuAction(() => handleEdit(row.id))}>
                                                            <ListItemIcon><EditIcon fontSize="small"
                                                                                    color="primary"/></ListItemIcon>
                                                            <ListItemText>{t('common.actions.edit')}</ListItemText>
                                                        </MenuItem>
                                                        <MenuItem
                                                            onClick={() => handleMenuAction(() => handleView(row.id))}>
                                                            <ListItemIcon><VisibilityIcon fontSize="small"
                                                                                          color="info"/></ListItemIcon>
                                                            <ListItemText>{t('employee.table.viewContracts')}</ListItemText>
                                                        </MenuItem>
                                                        <Divider/>
                                                        <MenuItem
                                                            onClick={() => handleMenuAction(() => handleAddContract(row.id))}
                                                            disabled={row.hasActiveContract}
                                                        >
                                                            <ListItemIcon><PostAddIcon fontSize="small"
                                                                                       color={row.hasActiveContract ? 'disabled' : 'success'}/></ListItemIcon>
                                                            <ListItemText>{t('employee.table.addContract')}</ListItemText>
                                                        </MenuItem>
                                                    </Menu>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                )}
        </>
    );
};

export default TableComponent;
